import { useState } from "react";
import axios from "axios";
import { Phone } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Chapter } from "./shared";

const INTERESTS = [
  "The Growth Plan — Website + SEO + Google Profile",
  "Custom Web App — Quoting, Booking, or Payments",
  "AI Agent Automation",
  "Not sure yet",
];

const inputCls =
  "w-full rounded-lg border border-[#e7e1d6] bg-[#faf7f2] px-4 py-3 text-base text-[#16324a] placeholder:text-[#627d98]/70 focus:outline-none focus:ring-2 focus:ring-[#c99a43]/50 focus:border-[#c99a43] transition";

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-[13px] font-semibold tracking-wide text-[#334e68]">{label}</span>
    {children}
  </label>
);

const Contact = () => {
  const [form, setForm] = useState({ name: "", business: "", phone: "", email: "", website: "", interest: INTERESTS[0] });
  const [sending, setSending] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, form);
      toast.success(`Thanks, ${form.name.split(" ")[0] || "there"}!`, {
        description:
          "Got it! We'll be in touch shortly. Need us right now? Call (610) 453-4448.",
        duration: 8000,
      });
      setForm({ name: "", business: "", phone: "", email: "", website: "", interest: INTERESTS[0] });
    } catch (err) {
      toast.error("Something went wrong sending your request", {
        description: "Please call us at (610) 453-4448 and we'll get you taken care of right away.",
        duration: 8000,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-24 lg:py-28 bg-[#0f2334]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center flex flex-col items-center gap-4 mb-12">
          <Chapter num="07" label="Get Started" dark />
          <Reveal delay={0.1}>
            <h2 className="text-3xl sm:text-4xl font-serif font-normal tracking-tight text-white">
              Ready for More Leads?
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="text-base sm:text-lg text-[#cdd8de]">
              Contact us for a no pressure, no obligation, clear picture of where your
              business stands online today.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <a
              data-testid="contact-phone-link"
              href="tel:6104534448"
              className="inline-flex items-center gap-2.5 font-serif text-2xl sm:text-3xl text-[#e8c78f] hover:text-white transition-colors duration-300"
            >
              <Phone size={22} /> (610) 453-4448
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="bg-white rounded-2xl p-6 sm:p-9 shadow-2xl grid sm:grid-cols-2 gap-5"
          >
            <Field label="Name">
              <input data-testid="contact-name-input" required type="text" placeholder="Jane Smith" value={form.name} onChange={set("name")} className={inputCls} />
            </Field>
            <Field label="Business Name">
              <input data-testid="contact-business-input" required type="text" placeholder="Smith's Hardware" value={form.business} onChange={set("business")} className={inputCls} />
            </Field>
            <Field label="Phone">
              <input data-testid="contact-phone-input" required type="tel" placeholder="(610) 555-0100" value={form.phone} onChange={set("phone")} className={inputCls} />
            </Field>
            <Field label="Email">
              <input data-testid="contact-email-input" required type="email" placeholder="jane@example.com" value={form.email} onChange={set("email")} className={inputCls} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Website (if you have one)">
                <input data-testid="contact-website-input" type="text" inputMode="url" placeholder="yourbusiness.com" value={form.website} onChange={set("website")} className={inputCls} />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="What Are You Interested In?">
                <select data-testid="contact-interest-select" value={form.interest} onChange={set("interest")} className={inputCls}>
                  {INTERESTS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </Field>
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="sm:col-span-2 w-full bg-[#c99a43] text-[#16324a] font-bold px-6 py-4 rounded-full hover:bg-[#e8c78f] transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sending ? "Sending…" : "I'm interested, call me!"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
